import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, ShieldCheck, Camera, FileText, BellRing, Target, Heart, Lock,
  ArrowRight, MessageSquareText, Crown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { trackEvent } from "../lib/analytics";
import { PremiumPill } from "../components/PremiumPill";
import { Button } from "../components/ui/button";

const GOLD_FRAME =
  "relative rounded-3xl border border-amber-300/50 bg-gradient-to-br from-amber-50/40 via-white to-white shadow-sm";
const GOLD_FRAME_HOVER =
  "hover:border-amber-400/70 hover:shadow-[0_10px_30px_-15px_rgba(11,27,61,0.35),0_0_0_1px_rgba(251,191,36,0.35)] transition";

/**
 * Card kinds:
 *  - "premium" : full-feature is server-side require_premium (locked for free users)
 *  - "boosted" : feature also has a free tier; premium adds extras
 */
const buildCards = (t) => [
  {
    id: "reactiecoach", kind: "premium", icon: MessageSquareText,
    title: t("premium.card.coach.title"), desc: t("premium.card.coach.desc"),
    freeHint: null, premiumHint: t("premium.card.coach.pro"),
    action: { to: "/reactiecoach", label: t("premium.card.open") },
  },
  {
    id: "foto", kind: "premium", icon: Camera,
    title: t("premium.card.photo.title"), desc: t("premium.card.photo.desc"),
    freeHint: null, premiumHint: t("premium.card.photo.pro"),
    action: { to: "/foto-analyse", label: t("premium.card.open") },
  },
  {
    id: "scam", kind: "boosted", icon: ShieldCheck,
    title: t("premium.card.scam.title"), desc: t("premium.card.scam.desc"),
    freeHint: t("premium.card.scam.free"), premiumHint: t("premium.card.scam.pro"),
    action: { to: "/scam-check", label: t("premium.card.openFree") },
  },
  {
    id: "match", kind: "boosted", icon: Target,
    title: t("premium.card.match.title"), desc: t("premium.card.match.desc"),
    freeHint: t("premium.card.match.free"), premiumHint: t("premium.card.match.pro"),
    action: { to: "/woningen", label: t("premium.card.openFree") },
  },
  {
    id: "vault", kind: "boosted", icon: FileText,
    title: t("premium.card.vault.title"), desc: t("premium.card.vault.desc"),
    freeHint: t("premium.card.vault.free"), premiumHint: t("premium.card.vault.pro"),
    action: { to: "/vault", label: t("premium.card.openFree") },
  },
  {
    id: "alerts", kind: "boosted", icon: BellRing,
    title: t("premium.card.alerts.title"), desc: t("premium.card.alerts.desc"),
    freeHint: t("premium.card.alerts.free"), premiumHint: t("premium.card.alerts.pro"),
    action: { to: "/alerts", label: t("premium.card.openFree") },
  },
  {
    id: "favs", kind: "boosted", icon: Heart,
    title: t("premium.card.favs.title"), desc: t("premium.card.favs.desc"),
    freeHint: t("premium.card.favs.free"), premiumHint: t("premium.card.favs.pro"),
    action: { to: "/woningen", label: t("premium.card.openFree") },
  },
];

const Card = ({ card, isPremium, t }) => {
  const Icon = card.icon;
  const locked = card.kind === "premium" && !isPremium;
  const onCardClick = () =>
    trackEvent("premium_tool_clicked", { tool: card.id, is_premium: isPremium, locked });
  const body = (
    <div
      className={`${GOLD_FRAME} ${!locked ? GOLD_FRAME_HOVER : ""} p-6 h-full flex flex-col`}
      data-testid={`premium-card-${card.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-navy text-amber-300 ring-1 ring-amber-300/40">
          <Icon size={20} />
        </span>
        <PremiumPill
          active={isPremium}
          labelActive={t("premium.pill.active")}
          labelInactive={t("premium.pill.inactive")}
        />
      </div>
      <h3 className="mt-4 font-display text-lg font-extrabold text-navy">{card.title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{card.desc}</p>
      <div className="mt-4 space-y-1.5 text-xs text-slate-500">
        {card.freeHint && (
          <div className="flex items-start gap-2" data-testid={`premium-card-${card.id}-free`}>
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
            <span><strong className="text-navy">{t("premium.card.freeLabel")}:</strong> {card.freeHint}</span>
          </div>
        )}
        {card.premiumHint && (
          <div className="flex items-start gap-2" data-testid={`premium-card-${card.id}-pro`}>
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
            <span><strong className="text-amber-700">{t("premium.card.proLabel")}:</strong> {card.premiumHint}</span>
          </div>
        )}
      </div>
      <div className="mt-auto flex items-center justify-between border-t border-amber-200/50 pt-4">
        {locked ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
              <Lock size={13} /> {t("premium.card.locked")}
            </span>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700">
              {t("premium.card.unlock")} <ArrowRight size={14} />
            </span>
          </>
        ) : (
          <>
            <span className="text-xs font-semibold text-slate-400">
              {card.kind === "boosted" ? t("premium.card.available") : t("premium.pill.inactive")}
            </span>
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-brandblue">
              {card.action.label} <ArrowRight size={14} />
            </span>
          </>
        )}
      </div>
    </div>
  );
  if (locked) {
    return <Link to="/prijzen" data-testid={`premium-card-${card.id}-link`} onClick={onCardClick}>{body}</Link>;
  }
  return <Link to={card.action.to} data-testid={`premium-card-${card.id}-link`} onClick={onCardClick}>{body}</Link>;
};

const PremiumHub = () => {
  const { t } = useT();
  const { user } = useAuth();
  useSeo({ title: t("premium.seo.title"), description: t("premium.seo.desc"), path: "/premium" });
  const isPremium = !!user?.is_premium;
  const cards = buildCards(t);

  return (
    <div data-testid="premium-hub-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-navy">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 -bottom-24 h-80 w-80 rounded-full bg-brandblue/30 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6">
          <div className="flex items-center gap-2 text-amber-300">
            <Crown size={18} />
            <span className="text-xs font-bold uppercase tracking-widest">WoningAlert</span>
          </div>
          <h1
            className="mt-3 font-display text-4xl font-extrabold text-white sm:text-5xl"
            data-testid="premium-hub-title"
          >
            {t("premium.hero.brand")} <span className="text-amber-300">Premium</span>
          </h1>
          {isPremium ? (
            <div className="mt-4 flex items-center gap-3" data-testid="premium-hero-active">
              <PremiumPill
                active
                size="md"
                labelActive={t("premium.pill.active")}
              />
              <p className="text-white/80">{t("premium.hero.activeSub")}</p>
            </div>
          ) : (
            <>
              <p className="mt-4 max-w-2xl text-lg text-white/80">{t("premium.hero.freeSub")}</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-amber-400 text-navy hover:bg-amber-300"
                  data-testid="premium-hero-cta"
                >
                  <Link to="/prijzen">
                    <Sparkles size={16} className="mr-2" /> {t("premium.hero.cta")}
                  </Link>
                </Button>
                {!user && (
                  <Button asChild variant="ghost" className="rounded-full text-white hover:bg-white/10">
                    <Link to="/login" data-testid="premium-hero-login">{t("nav.login")}</Link>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Cards grid */}
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c) => <Card key={c.id} card={c} isPremium={isPremium} t={t} />)}
        </div>

        {/* Foot CTA for free users only */}
        {!isPremium && (
          <div className={`${GOLD_FRAME} mt-10 p-8 text-center sm:p-10`} data-testid="premium-foot-cta-box">
            <Crown className="mx-auto text-amber-500" size={32} />
            <h2 className="mt-4 font-display text-2xl font-extrabold text-navy">{t("premium.footcta.title")}</h2>
            <p className="mx-auto mt-2 max-w-xl text-slate-600">{t("premium.footcta.sub")}</p>
            <Button
              asChild
              size="lg"
              className="mt-6 rounded-full bg-navy text-amber-300 hover:bg-navy/90"
              data-testid="premium-foot-cta"
            >
              <Link to="/prijzen">{t("premium.hero.cta")}</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumHub;
